import React, { useState, useRef, useCallback, useContext } from 'react';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import { ADD_UNIT } from '../../../Reducers/Actions';
import { Box } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../theme';
import FormInput from '../../../SystemDesign/FormInput';
import { useCreateUnitMutation } from '../../../services/SkillsManagement';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import AddIcon from '@mui/icons-material/Add';
import ColoredSelector from '../../../SystemDesign/ColoredSelector';

const AddUnit = ({ open, onClose, onSubmit, disciplines = [], selectedDiscipline = null }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Use SnackBar context for displaying messages
  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);
  
  // State to track form validity and dirty state
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [selectedDisciplineId, setSelectedDisciplineId] = useState(
    selectedDiscipline ? (selectedDiscipline.discipline_id || selectedDiscipline.id) : null
  );
  
  // Update selectedDisciplineId when selectedDiscipline changes
  React.useEffect(() => {
    if (selectedDiscipline) {
      const disciplineId = selectedDiscipline.discipline_id || selectedDiscipline.id;
      setSelectedDisciplineId(disciplineId);
      
      // Update form values while preserving existing values
      setFormValues(currentValues => ({
        ...currentValues,
        discipline_id: disciplineId
      }));
    }
  }, [selectedDiscipline]);
  
  // Create a ref to access Formik methods from outside the Formik component
  const formRef = useRef(null);
  
  // RTK Query mutation hook
  const [createUnit, { isLoading }] = useCreateUnitMutation();
  
  // Validation schema
  const validationSchema = Yup.object({
    code: Yup.string().required('Unit code is required'),
    title: Yup.string().required('Unit title is required'),
    discipline_id: Yup.number().required('Departement is required').typeError('Departement must be selected')
  });
  
  // Function to trigger form submission via MuiDialog action button
  const submitForm = useCallback(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, []);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      // Start submission
      setSubmitting(true);
      
      // Ensure discipline_id is properly formatted (number) for the backend API
      const discipline_id = values.discipline_id ? parseInt(values.discipline_id, 10) : 0;
      
      // Call the API to create the unit
      const result = await createUnit({
        body: {
          unit_code: values.code.trim(),
          specific_unit: values.title.trim(),
          discipline_id: discipline_id
        }
      }).unwrap();
      
      // The unit data is nested inside the 'data' property of the response
      const unitData = result.data || {};
      const unitId = unitData.unit_id;
      
      if (!unitId) {
        // Show error in SnackBar if no unit_id was returned
        setInfoAlert({
          severity: 'error',
          message: 'Could not create unit: No unit ID returned from server'
        });
        setOpenSnackBar(true);
        setSubmitting(false);
        return;
      }
      
      // Create a proper unit object with all required fields
      const unitForStore = {
        unit_id: unitId,
        code: unitData.unit_code || values.code,
        title: unitData.specific_unit || values.title,
        discipline_id: discipline_id, // Using number format for discipline_id
        createdAt: unitData.createdAt,
        updatedAt: unitData.updatedAt,
        skills: []
      };
      

      dispatch({ type: ADD_UNIT, payload: unitForStore });
      
      // Notify parent component
      if (onSubmit) {
        onSubmit(unitForStore);
      }
      
      // Show success message in SnackBar
      setInfoAlert({
        severity: 'success',
        message: `Unit "${values.code} - ${values.title}" successfully created`
      });
      setOpenSnackBar(true);
      
      // Reset form and close dialog
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating unit:', error);
      // Show error message in SnackBar instead of inline form error
      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || 'Failed to create unit'
      });
      setOpenSnackBar(true);
    } finally {
      // Always make sure to finish submission state
      setSubmitting(false);
    }
  };

  // State to preserve form values between reinitializations
  const [formValues, setFormValues] = useState({
    code: '',
    title: '',
    discipline_id: selectedDisciplineId || ''
  });

  // Create dialog content for MuiDialog
  const dialogContent = () => (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
      validateOnMount={true}
      enableReinitialize={false}
    >
      {(formikProps) => {
        // Update parent about form state changes without using hooks
        const { isValid, dirty, setFieldValue } = formikProps;
        if (formRef.current && (formRef.current.isValid !== isValid || formRef.current.dirty !== dirty)) {
          setTimeout(() => {
            setIsFormValid(isValid);
            setIsFormDirty(dirty);
          }, 0);
        }
        
        // Handle discipline selection
        const handleDisciplineChange = (disciplineId) => {
          setSelectedDisciplineId(disciplineId);
          setFieldValue('discipline_id', disciplineId);
          
          // Update the preserved form values state with current values plus new discipline
          setFormValues({
            ...formikProps.values,
            discipline_id: disciplineId
          });
        };
        
        return (
          <Form id="add-unit-form">  
            {/* Form fields */}
            <Box sx={{ mb: 3 }}>
              <FormInput
                name="code"
                label="Unit Code"
                placeholder="Ex: H01, D01..."
                variant="outlined"
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <FormInput
                name="title"
                label="Unit Name"
                placeholder="Ex: Work Preparation & Debrief..."
                variant="outlined"
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <ColoredSelector
                options={disciplines}
                value={formikProps.values.discipline_id}
                onChange={handleDisciplineChange}
                placeholder="Select a departement"
                label="Departement"
                colorKey="color"
                valueKey="discipline_id"
                labelKey="name"
                searchable={true}
                required={true}
                error={formikProps.touched.discipline_id && Boolean(formikProps.errors.discipline_id)}
                helperText={formikProps.touched.discipline_id && formikProps.errors.discipline_id}
              />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <MuiDialog 
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      icon={<AddIcon />}
      title="Create New Unit"
      subtitle="Add a new unit to an existing departement"
      content={dialogContent}
      existAction={true}
      actionTitle="Create Unit"
      actionFunction={submitForm}
      isLoading={isLoading}
      actionType="submit"
      formId="add-unit-form"
      disableAction={!(isFormValid && isFormDirty)}
    />
  );
};

export default AddUnit;